import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { DURATION, EASE, TRAVEL } from './tokens'

/**
 * `<Reveal>` — the workhorse cinematic primitive. Children fade up into
 * place the first time the element scrolls into view. Used for copy
 * blocks, tiles, single hero sections — anything that should "arrive"
 * with intent rather than appear.
 *
 * Each Reveal fires ONCE per mount (`viewport.once`) so scrolling
 * back-and-forth doesn't restart the choreography. Under
 * `prefers-reduced-motion` the travel collapses to a plain opacity fade.
 */
export function Reveal({
  children,
  /** Travel distance in px. Default = standard (24). */
  travel = TRAVEL.standard,
  /** Duration in seconds. Default = standard (0.7s). */
  duration = DURATION.standard,
  /** Optional delay so a series of Reveals can be hand-paced. */
  delay = 0,
  /** Viewport margin — fire a little before the element is fully visible. */
  amount = 0.15,
  className,
  as = 'div'
}: {
  readonly children: ReactNode
  readonly travel?: number
  readonly duration?: number
  readonly delay?: number
  readonly amount?: number
  readonly className?: string
  readonly as?: 'div' | 'section' | 'article' | 'header' | 'footer' | 'li'
}): JSX.Element {
  const reduce = useReducedMotion() === true
  const Tag = motion[as]
  return (
    <Tag
      className={className}
      initial={{ opacity: 0, y: reduce ? 0 : travel }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount }}
      transition={{ duration: reduce ? 0.2 : duration, delay, ease: EASE }}
    >
      {children}
    </Tag>
  )
}
